import { Box, Card, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import SkeletonLoader from '@components/SkeletonLoader';

import { ALERTS, PATIENTS } from '@constants/routes';

import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import { useGetAccountKpi } from 'generated/account.generated';

const messages = defineMessages({
  activePatients: {
    id: 'dashboard.practitioner.kpi.activePatients',
    defaultMessage: 'Active Patients',
  },
  patientsWithAlertsSetup: {
    id: 'dashboard.practitioner.kpi.patientsWithAlertsSetup',
    defaultMessage: 'Patients With Alerts Set Up',
  },
  alertsWithin24Hours: {
    id: 'dashboard.practitioner.kpi.alertsWithin24Hours',
    defaultMessage: 'Alerts In Last 24 Hours',
  },
  openAlerts: {
    id: 'dashboard.practitioner.kpi.openAlerts',
    defaultMessage: 'Open Alerts',
  },
  patientsWithDevices: {
    id: 'dashboard.practitioner.kpi.patientsWithDevices',
    defaultMessage: 'Patients With Health Devices',
  },
});

type KpiModel = {
  key: string;
  title: MessageDescriptor;
  value: number;
  handleClick?: () => void;
  className: string;
};

export default function Kpi() {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const {
    state: { loggedInProfile },
  } = useSession();
  const [account_id] = [loggedInProfile?.account_id ?? ''];

  const { data: accountKpis, loading } = useGetAccountKpi({
    variables: {
      input: {
        account_id,
      },
    },
    skip: !account_id,
  });

  const kpis = Object.entries(accountKpis?.getAccountRecord.kpi ?? {}).reduce<
    KpiModel[]
  >((acc, [k, v]) => {
    if (typeof v !== 'number') {
      return acc;
    }
    let input:
      | (Pick<KpiModel, 'handleClick' | 'title'> & {
          className: string;
          index: number;
        })
      | undefined;
    switch (k) {
      case 'active_patients':
        input = {
          title: messages.activePatients,
          handleClick: () => navigateRoute(PATIENTS),
          index: 2,
          className: 'c',
        };
        break;
      case 'patients_with_alerts':
        input = {
          title: messages.patientsWithAlertsSetup,
          handleClick: () => navigateRoute(PATIENTS),
          index: 3,
          className: 'd',
        };
        break;
      case 'alerts_within_24_hours':
        input = {
          title: messages.alertsWithin24Hours,
          handleClick: () => navigateRoute(ALERTS),
          index: 1,
          className: 'b',
        };
        break;
      case 'open_alerts':
        input = {
          title: messages.openAlerts,
          handleClick: () => navigateRoute(ALERTS),
          index: 0,
          className: 'a',
        };
        break;
      case 'patients_with_devices':
        input = {
          title: messages.patientsWithDevices,
          handleClick: undefined,
          index: 4,
          className: 'e',
        };
        break;
    }
    if (input) {
      const { index, ...restInput } = input;
      acc.splice(index, 0, { ...restInput, key: k, value: v });
    }
    return acc;
  }, []);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: {
          xs: `
          "a" 
          "b"
          "c"
          "d"
          "e"
          `,
          sm: `
            "a b"
            "c d"
            "e ."
          `,
          md: `
            "a a a b b b"
            "c c d d e e"
          `,
          xl: `"a b c d e" "a b c d e"`,
        },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(6, 1fr)',
          xl: 'repeat(5, 1fr)',
        },
        gap: 2,
        width: '100%',
        my: 3,
      }}
    >
      <SkeletonLoader
        count={kpis.length}
        height={111}
        loading={loading}
        width="100%"
      >
        {kpis.map(({ className, key, handleClick, title, value }) => (
          <Card
            key={key}
            onClick={handleClick}
            sx={({ palette: { grey } }) => ({
              height: 111,
              border: `1px solid ${grey[200]}`,
              textAlign: 'center',
              alignContent: 'center',
              '&:hover': {
                cursor: handleClick ? 'pointer' : undefined,
                backgroundColor: handleClick ? grey.A100 : undefined,
              },
              gridArea: className,
            })}
          >
            <Typography variant="overline">{formatMessage(title)}</Typography>
            <Typography variant="h5">{value}</Typography>
          </Card>
        ))}
      </SkeletonLoader>
    </Box>
  );
}
